import React, { useState, useMemo, useEffect, useRef } from 'react';
import './PromptLibrary.css';
import Modal from '../Modal/Modal';
import DropdownSelector from '../DropdownSelector/DropdownSelector';
import SearchBar from '../SearchBar/SearchBar';
import { toast } from 'react-toastify';

import filterIcon from '../../assets/new-images/filter-icon.svg';
import copyIcon from '../../assets/new-images/copy-icon.svg';
import editIcon from '../../assets/new-images/edit-icon.svg';
import deleteIcon from '../../assets/new-images/delete-icon.svg';
import closeIcon from '../../assets/new-images/close-icon.svg';
import threeDotsIcon from '../../assets/images/three-dots-icon.svg';

import usePrompts from '../../hooks/usePrompts';
import useOutsideClick from '../../hooks/useOutsideClick';
import { useConstants } from '../../constant/constantProvider';
import { KEYS } from '../../constant/keyConstants';

const PromptLibrary = ({ open, onClose, onSelect, onEdit, onDelete }) => {
  const { global, chat, COPY_SUCCESS } = useConstants();
  const [activeTab, setActiveTab] = useState('Personal');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [sortKey, setSortKey] = useState('latest');

  const SORT_OPTIONS = useMemo(() => [
    { key: 'latest', label: chat.SORT_LATEST },
    { key: 'earliest', label: chat.SORT_EARLIEST },
  ], [chat]);

  const isPersonal = activeTab === 'Personal';

  // Fetch both types when the library is open to ensure data is ready for both tabs
  // force: true ensures fresh data every time the library is opened
  const { prompts: personalPrompts, loading: loadingPersonal } = usePrompts(KEYS.PROMPT.PERSONAL, { enabled: open, force: true });
  const { prompts: suggestedPrompts, loading: loadingSuggested } = usePrompts(KEYS.PROMPT.SUGGESTED, { enabled: open, force: true });

  const prompts = isPersonal ? personalPrompts : suggestedPrompts;
  const loading = isPersonal ? loadingPersonal : loadingSuggested;

  // Refetch data and reset filters every time the modal is opened
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedDept(null);
      setSelectedTask(null);
      setOpenMenuId(null);
      setSortOpen(false);
      setSortKey('latest');
    }
  }, [open]);

  // Outside-click closes sort dropdown
  const sortBtnRef = useRef(null);
  useOutsideClick(sortBtnRef, () => setSortOpen(false));

  // Reset all filters when switching tabs
  useEffect(() => {
    setSearchTerm('');
    setSelectedDept(null);
    setSelectedTask(null);
    setOpenMenuId(null);
    setSortOpen(false);
    setSortKey('latest');
  }, [activeTab]);

  // Derived dropdown options (de-duped, from live data)
  const departmentOptions = useMemo(() => {
    const unique = new Set();
    prompts.forEach(p => {
      const dept = p.department || p.dept;
      if (dept) unique.add(String(dept).trim());
    });
    return Array.from(unique).map(name => ({ name }));
  }, [prompts]);

  const taskOptions = useMemo(() => {
    const unique = new Set();
    prompts.forEach(p => {
      const task = p.task || p.task_name || p.taskName;
      if (task) unique.add(String(task).trim());
    });
    return Array.from(unique).map(name => ({ name }));
  }, [prompts]);

  // Search + dept/task filter + sort
  const filteredPrompts = useMemo(() => {
    const searched = (prompts || []).filter(p => {
      const matchesSearch = !searchTerm ||
        p.prompt_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.prompt_text?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesDept = !selectedDept || 
        p.department?.toLowerCase() === (selectedDept.name || selectedDept).toLowerCase();
      
      const matchesTask = !selectedTask || 
        p.task?.toLowerCase() === (selectedTask.name || selectedTask).toLowerCase();

      return matchesSearch && matchesDept && matchesTask;
    });

    if (!isPersonal) return searched;

    return [...searched].sort((a, b) => {
      const da = new Date(a.edit_time || 0);
      const db = new Date(b.edit_time || 0);
      return sortKey === 'latest' ? db - da : da - db;
    });
  }, [prompts, searchTerm, selectedDept, selectedTask, isPersonal, sortKey]);

  const handleAction = (e, action, prompt) => {
    e.stopPropagation();
    setOpenMenuId(null);
    if (action === 'copy') {
      toast.info(COPY_SUCCESS || "Copied to clipboard!", { autoClose: 1000 });
      navigator.clipboard.writeText(prompt.prompt_text || prompt.description || '');
    } else if (action === 'edit') {
      onEdit && onEdit(prompt);
    } else if (action === 'delete') {
      onDelete && onDelete(prompt);
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedDept(null);
    setSelectedTask(null);
  };

  const isFilterActive = searchTerm !== '' || selectedDept !== null || selectedTask !== null;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="prompt-library-modal" onClick={() => { setOpenMenuId(null); setSortOpen(false); }}>

        {/* Header */}
        <div className="prompt-library-header">
          <h2>{chat.PROMPT_LIBRARY_TITLE}</h2>
          <button className="close-btn" onClick={onClose}>
            <img src={closeIcon} alt="close" />
          </button>
        </div>

        {/* Tabs */}
        <div className="prompt-library-tabs">
          <button
            className={`tab-item ${activeTab === 'Personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('Personal')}
          >
            {chat.PERSONAL_TAB}
          </button>
          <button
            className={`tab-item ${activeTab === 'Suggested' ? 'active' : ''}`}
            onClick={() => setActiveTab('Suggested')}
          >
            {chat.SUGGESTED_TAB}
          </button>
        </div>

        {/* Filters */}
        <div className="prompt-library-filters-row prompt">
          <div className="search-sort-group">
            <div className="search-bar-wrapper">
              <SearchBar
                value={searchTerm}
                onChange={setSearchTerm}
                onClear={() => setSearchTerm('')}
              />
            </div>

            {/* Sort button — Personal tab only */}
            {isPersonal && (
              <div className="sort-anchor" ref={sortBtnRef}>
                <button
                  className={`filter-settings-btn ${sortOpen ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setSortOpen(v => !v); }}
                  title="Sort prompts"
                >
                  <img src={filterIcon} alt="sort" />
                </button>

                {/* Figma-style sort dropdown using global classes */}
                {sortOpen && (
                  <div className="global-action-dropdown dropdown-fade" onClick={e => e.stopPropagation()}>
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.key}
                        className={`global-action-item ${sortKey === opt.key ? 'selected' : ''}`}
                        onClick={() => { setSortKey(opt.key); setSortOpen(false); }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="filter-dropdown-wrapper">
            <DropdownSelector
              items={departmentOptions}
              selected={selectedDept}
              onChange={(val) => setSelectedDept(val)}
              placeholder={{ name: chat.DEPARTMENT }}
              theme="default"
              type="selected"
            />
          </div>

          <div className="filter-dropdown-wrapper">
            <DropdownSelector
              items={taskOptions}
              selected={selectedTask}
              onChange={(val) => setSelectedTask(val)}
              placeholder={{ name: chat.TASK }}
              theme="default"
              type="selected"
            />
          </div>

          {isFilterActive && (
            <button className="reset-btn" onClick={handleReset}>
              {global.RESET}
            </button>
          )}
        </div>

        {/* Grid */}
        <div className="prompt-grid thin-scroll">
          {loading ? (
            <div className="no-results">{chat.LOADING_PROMPTS}</div>
          ) : filteredPrompts?.length > 0 ? (
            filteredPrompts.map((prompt) => (
              <div
                key={prompt.id}
                className="prompt-card"
                onClick={() => onSelect && onSelect(prompt.prompt_text || prompt.description)}
              >
                <div className="prompt-card-content">
                  <div className="prompt-card-title">{prompt.prompt_name}</div>
                  <div className="prompt-card-description">{prompt.prompt_text}</div>
                </div>

                <div className="prompt-card-actions">
                  <button
                    className="more-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === prompt.id ? null : prompt.id);
                    }}
                  >
                    <img src={threeDotsIcon} alt="more" />
                  </button>

                  {openMenuId === prompt.id && (
                    <div className="global-action-dropdown dropdown-fade" onClick={(e) => e.stopPropagation()}>
                      {/* Copy — always visible */}
                      <button className="global-action-item" onClick={(e) => handleAction(e, 'copy', prompt)}>
                        <img src={copyIcon} alt="copy" />
                        {global.COPY}
                      </button>

                      {/* Edit & Delete — Personal tab only */}
                      {isPersonal && (
                        <>
                          <button className="global-action-item" onClick={(e) => handleAction(e, 'edit', prompt)}>
                            <img src={editIcon} alt="edit" />
                            {global.EDIT || 'Edit'}
                          </button>
                          <button className="global-action-item delete" onClick={(e) => handleAction(e, 'delete', prompt)}>
                            <img src={deleteIcon} alt="delete" />
                            {global.DELETE}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">{chat.NO_PROMPTS_FOUND}</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PromptLibrary;